import type {
  AutomationAction,
  AutomationRule,
  ConditionGroup,
  EvaluationRule,
  EvaluationRuleUpdateInput,
  EvaluatorTrigger as EvaluatorTriggerModel,
  EventOutput,
  Filter,
  RuleUpdateInput,
} from '@/adapters/apiv1.g';
import type {
  Automation,
  Evaluator,
  EvaluatorTrigger,
  RuleAction,
  RuleAggregation,
  RuleCondition,
  RuleConditionGroup,
  RuleConditionType,
  RuleFilter,
  RuleFilterOperator,
  RuleOutput,
  RulePattern,
  RuleSignalOutput,
  RuleTrigger,
  WorkflowRule,
} from '@/types/core/workflow-rule';
import { isRuleFilterOperator } from '@/constants/engine';
import type { UUID } from '@/utils/uuid';

/**
 * Raw backend shape for a WorkflowRule.
 * This is a UI-facing superset of the current evaluation and automation payloads.
 */
export type WorkflowRuleModel = {
  _rev?: number;
  rule_id?: string;
  tenant_id?: string;
  name?: string;
  description?: string;
  type?: 'derivation' | 'aggregation' | 'pattern';
  enabled?: boolean;
  priority?: number;
  triggers?: Array<{
    event_type?: string;
    event_space?: string;
    filters?: Array<{
      field?: string;
      operator?: string;
      value?: unknown;
    }>;
    priority?: number;
    window?: {
      threshold?: number;
      duration_seconds?: number;
    };
  }>;
  trigger?: {
    event_type?: string;
    event_space?: string;
  };
  filters?: Array<{
    field?: string;
    operator?: string;
    value?: unknown;
  }>;
  condition?: {
    type?: string;
    parameters?: Record<string, unknown>;
  };
  aggregation?: {
    function?: string;
    window?: number;
    group_by?: string[];
    threshold?: number;
    threshold_operator?: string;
  };
  pattern?: {
    sequence?: Array<Record<string, unknown>>;
    max_duration?: number;
  };
  outputs?: Array<{
    event_type?: string;
    severity?: string;
    target_refs?: string[];
    payload_template?: Record<string, unknown>;
  }>;
  action?: {
    connector_ref?: string;
    feature_ref?: string;
    payload_template?: Record<string, unknown>;
    input_mapping?: {
      fields?: Array<{
        to?: string;
        from?: string;
      }>;
    };
    idempotency_template?: string;
  };
  status?: string;
  version?: number;
  published_version?: number;
  created_at?: string;
  updated_at?: string;
};

const TEMPLATE_PAYLOAD_KEY = 'template';

// ─── Sub-type codecs ──────────────────────────────────────────────────────────
// Each pair (extract / serialize) lives together so both sides are easy to keep
// in sync when the backend contract changes.

type TriggerModel = WorkflowRuleModel['trigger'];
type FilterModel = NonNullable<WorkflowRuleModel['filters']>[number];
type ConditionModel = WorkflowRuleModel['condition'];
type AggregationModel = WorkflowRuleModel['aggregation'];
type PatternModel = WorkflowRuleModel['pattern'];
type EventOutputModel = NonNullable<WorkflowRuleModel['outputs']>[number];

function extractTrigger(m: TriggerModel): RuleTrigger | undefined {
  if (!m) return undefined;
  return { eventType: m.event_type ?? '', eventSpace: m.event_space ?? '' };
}
function serializeTrigger(t: RuleTrigger | undefined): TriggerModel {
  if (!t) return undefined;
  return { event_type: t.eventType, event_space: t.eventSpace };
}

function extractFilter(m: FilterModel): RuleFilter {
  return {
    field: m.field ?? '',
    operator: extractFilterOperator(m.operator),
    value: m.value,
  };
}

// The wire value is checked against the known operator set rather than cast
// blind: a value this build does not recognize (a backend operator added after
// this client was generated) falls back to 'equals' with a warning instead of
// becoming a valid-looking operator the editor would silently rewrite on save.
function extractFilterOperator(value: string | undefined): RuleFilterOperator {
  if (isRuleFilterOperator(value)) return value;
  if (value != null && value !== '') {
    console.warn(
      `unknown filter operator "${value}" from the engine; defaulting to equals`
    );
  }
  return 'equals';
}
function serializeFilter(f: RuleFilter): FilterModel {
  return { field: f.field, operator: f.operator, value: f.value };
}

function extractEvaluatorTrigger(m: EvaluatorTriggerModel): EvaluatorTrigger {
  return {
    eventType: m.event_type ?? '',
    eventSpace: m.event_space ?? '',
    filters: m.filters?.map(extractApiFilter) ?? [],
    priority: m.priority ?? 0,
    window: m.window
      ? {
          threshold: m.window.threshold ?? 0,
          durationSeconds: m.window.duration_seconds ?? 0,
        }
      : undefined,
  };
}
function serializeEvaluatorTrigger(t: EvaluatorTrigger): EvaluatorTriggerModel {
  return {
    event_type: t.eventType,
    event_space: t.eventSpace,
    filters: (t.filters ?? []).map((f: RuleFilter) => ({
      field: f.field,
      operator: f.operator,
      value: String(f.value),
    })),
    priority: t.priority,
    window: t.window
      ? {
          threshold: t.window.threshold,
          duration_seconds: t.window.durationSeconds,
        }
      : undefined,
  };
}

function extractCondition(m: ConditionModel): RuleCondition | undefined {
  if (!m) return undefined;
  return {
    type: (m.type ?? '') as RuleConditionType,
    parameters: m.parameters ?? {},
  };
}
function serializeCondition(c: RuleCondition | undefined): ConditionModel {
  if (!c) return undefined;
  return { type: c.type, parameters: c.parameters };
}

function extractAggregation(m: AggregationModel): RuleAggregation | undefined {
  if (!m) return undefined;
  return {
    function: m.function ?? '',
    window: m.window ?? 0,
    groupBy: m.group_by ?? [],
    threshold: m.threshold ?? 0,
    thresholdOperator: m.threshold_operator ?? '>=',
  };
}
function serializeAggregation(
  a: RuleAggregation | undefined
): AggregationModel {
  if (!a) return undefined;
  return {
    function: a.function,
    window: a.window,
    group_by: a.groupBy,
    threshold: a.threshold,
    threshold_operator: a.thresholdOperator,
  };
}

function extractPattern(m: PatternModel): RulePattern | undefined {
  if (!m) return undefined;
  return {
    sequence: (m.sequence ?? []).map((s) => s),
    maxDuration: m.max_duration ?? 0,
  };
}
function serializePattern(p: RulePattern | undefined): PatternModel {
  if (!p) return undefined;
  return { sequence: p.sequence, max_duration: p.maxDuration };
}

function extractConditionGroup(
  m: ConditionGroup | undefined
): RuleConditionGroup | undefined {
  if (!m) return undefined;
  return {
    logic: m.logic,
    filters: m.filters?.map(extractApiFilter),
    groups: m.groups?.map(extractConditionGroup).filter(Boolean) as
      | RuleConditionGroup[]
      | undefined,
  };
}
function serializeConditionGroup(
  g: RuleConditionGroup | undefined
): ConditionGroup | undefined {
  if (!g) return undefined;
  return {
    logic: g.logic,
    filters: g.filters?.map((f: RuleFilter) => ({
      field: f.field,
      operator: f.operator,
      value: String(f.value),
    })),
    groups: g.groups?.map(serializeConditionGroup).filter(Boolean) as
      | ConditionGroup[]
      | undefined,
  };
}

function extractSignalOutput(m: EventOutput): RuleSignalOutput {
  return {
    eventType: m.event_type ?? '',
    payloadTemplate: m.payload_template,
  };
}
export function serializeSignalOutput(o: RuleSignalOutput): EventOutput {
  return {
    event_type: o.eventType,
    payload_template: o.payloadTemplate as Record<string, string> | undefined,
  };
}

function extractAutomationAction(m: AutomationAction): RuleAction {
  return {
    connectorRef: m.connector_ref,
    featureRef: m.feature_ref,
    payloadTemplate: m.payload_template,
    inputMapping: m.input_mapping
      ? {
          fields: m.input_mapping.fields?.map((f) => ({
            from: f.from,
            to: f.to,
          })),
        }
      : undefined,
    idempotencyTemplate: m.idempotency_template,
  };
}
function serializeAutomationAction(a: RuleAction): AutomationAction {
  return {
    connector_ref: a.connectorRef,
    feature_ref: a.featureRef,
    payload_template: a.payloadTemplate as Record<string, string> | undefined,
    input_mapping: a.inputMapping
      ? {
          fields: a.inputMapping.fields?.map(
            (f: { from?: string; to?: string }) => ({
              from: f.from,
              to: f.to,
            })
          ),
        }
      : undefined,
    idempotency_template: a.idempotencyTemplate,
  };
}

function extractEventOutput(
  output: EventOutputModel | undefined
): Extract<RuleOutput, { type: 'event' }> {
  const payloadTemplate = (output?.payload_template ?? {}) as Record<
    string,
    unknown
  >;
  const context: Record<string, unknown> = { ...payloadTemplate };
  const templateValue = context[TEMPLATE_PAYLOAD_KEY];
  const template =
    typeof templateValue === 'string'
      ? templateValue
      : String(templateValue ?? '');
  delete context[TEMPLATE_PAYLOAD_KEY];

  return {
    type: 'event',
    eventOutput: {
      eventType: output?.event_type ?? '',
      template,
      context,
    },
  };
}

function serializeEventOutput(
  output: Extract<RuleOutput, { type: 'event' }>
): WorkflowRuleModel['outputs'] {
  const payloadTemplate: Record<string, unknown> = {
    ...(output.eventOutput.context ?? {}),
  };
  if (output.eventOutput.template.trim() !== '') {
    payloadTemplate[TEMPLATE_PAYLOAD_KEY] = output.eventOutput.template;
  }

  return [
    {
      event_type: output.eventOutput.eventType,
      payload_template:
        Object.keys(payloadTemplate).length > 0 ? payloadTemplate : undefined,
    },
  ];
}

function extractOutput(model: WorkflowRuleModel): RuleOutput | undefined {
  if (model.action) {
    return {
      type: 'action',
      actionOutput: {
        actions: [model.action],
        continueOnError: false,
      },
    };
  }
  return extractEventOutput(model.outputs?.[0]);
}
function serializeOutput(
  o: RuleOutput | undefined
): Pick<WorkflowRuleModel, 'outputs' | 'action'> {
  if (!o) return {};
  if (o.type === 'action') {
    const [firstAction] = o.actionOutput.actions;
    return {
      action:
        firstAction && typeof firstAction === 'object'
          ? (firstAction as WorkflowRuleModel['action'])
          : undefined,
    };
  }
  return { outputs: serializeEventOutput(o) };
}

// ─── Top-level converters ─────────────────────────────────────────────────────

/**
 * Transform a backend WorkflowRuleModel into a frontend WorkflowRule.
 */
export function extractWorkflowRule(model: WorkflowRuleModel): WorkflowRule {
  return {
    _rev: model._rev ?? model.version,
    ruleId: model.rule_id as UUID | undefined,
    tenantId: model.tenant_id as UUID | undefined,
    name: model.name,
    description: model.description,
    type: model.type,
    enabled: model.enabled,
    priority: model.priority,
    triggers: model.triggers?.map((t) => ({
      eventType: t.event_type ?? '',
      eventSpace: t.event_space ?? '',
      filters: t.filters?.map(extractFilter) ?? [],
      priority: t.priority ?? 0,
      window: t.window
        ? {
            threshold: t.window.threshold ?? 0,
            durationSeconds: t.window.duration_seconds ?? 0,
          }
        : undefined,
    })),
    trigger: extractTrigger(model.trigger),
    filters: model.filters?.map(extractFilter),
    condition: extractCondition(model.condition),
    aggregation: extractAggregation(model.aggregation),
    pattern: extractPattern(model.pattern),
    output: extractOutput(model),
  };
}

/**
 * Transform a frontend WorkflowRule into a backend WorkflowRuleModel.
 */
export function serializeWorkflowRule(rule: WorkflowRule): WorkflowRuleModel {
  return {
    _rev: rule._rev,
    rule_id: rule.ruleId,
    tenant_id: rule.tenantId,
    name: rule.name,
    description: rule.description,
    type: rule.type,
    enabled: rule.enabled,
    priority: rule.priority,
    triggers: rule.triggers?.map((t: EvaluatorTrigger) => ({
      event_type: t.eventType,
      event_space: t.eventSpace,
      filters: (t.filters ?? []).map((f: RuleFilter) => ({
        field: f.field,
        operator: f.operator,
        value: f.value,
      })),
      priority: t.priority,
      window: t.window
        ? {
            threshold: t.window.threshold,
            duration_seconds: t.window.durationSeconds,
          }
        : undefined,
    })),
    trigger: serializeTrigger(rule.trigger),
    filters: rule.filters?.map(serializeFilter),
    condition: serializeCondition(rule.condition),
    aggregation: serializeAggregation(rule.aggregation),
    pattern: serializePattern(rule.pattern),
    ...serializeOutput(rule.output),
  };
}

/**
 * Narrow a WorkflowRule to an Evaluator (derivation + event output).
 */
export function isEvaluator(rule: WorkflowRule): rule is Evaluator {
  return rule.type === 'derivation' && rule.output?.type === 'event';
}

/**
 * Narrow a WorkflowRule to an Automation (has a concrete action).
 */
export function isAutomation(rule: WorkflowRule): rule is Automation {
  return rule.action !== undefined;
}

// ─── Real API extractors ──────────────────────────────────────────────────────

function extractApiFilter(f: Filter): RuleFilter {
  return {
    field: f.field ?? '',
    operator: extractFilterOperator(f.operator),
    value: f.value,
  };
}

/**
 * Map a real API EvaluationRule to the UI WorkflowRule domain type.
 */
export function extractEvaluationRule(model: EvaluationRule): WorkflowRule {
  const firstOutput = model.outputs?.[0];
  const output: RuleOutput | undefined = firstOutput
    ? {
        type: 'event',
        eventOutput: {
          eventType: firstOutput.event_type ?? '',
          template: '',
          context: {},
        },
      }
    : undefined;

  return {
    _rev: model.version,
    ruleId: model.rule_id as UUID | undefined,
    tenantId: model.tenant_id as UUID | undefined,
    name: model.name,
    description: model.description,
    type: (model.type ?? 'derivation') as WorkflowRule['type'],
    enabled: model.enabled,
    priority: model.priority,
    version: model.version,
    status: model.status,
    triggers: model.triggers?.map(extractEvaluatorTrigger),
    trigger: model.trigger
      ? {
          eventType: model.trigger.event_type ?? '',
          eventSpace: model.trigger.event_space ?? '',
        }
      : undefined,
    filters: model.filters?.map(extractApiFilter),
    condition: model.condition
      ? {
          type: (model.condition.type ?? '') as RuleConditionType,
          parameters: model.condition.parameters ?? {},
        }
      : undefined,
    conditionGroup: extractConditionGroup(model.condition_group),
    aggregation: extractAggregation(model.aggregation as AggregationModel),
    pattern: extractPattern(model.pattern as PatternModel),
    publishedVersion: model.published_version,
    outputs: model.outputs?.map(extractSignalOutput),
    output,
  };
}

/**
 * Map a real API AutomationRule to the UI WorkflowRule domain type.
 */
export function extractAutomationRule(model: AutomationRule): WorkflowRule {
  return {
    _rev: model.version,
    ruleId: model.rule_id as UUID | undefined,
    tenantId: model.tenant_id as UUID | undefined,
    name: model.name,
    description: model.description,
    enabled: model.enabled,
    priority: model.priority,
    version: model.version,
    trigger: model.trigger
      ? {
          eventType: model.trigger.event_type ?? '',
          eventSpace: model.trigger.event_space ?? '',
        }
      : undefined,
    filters: model.filters?.map(extractApiFilter),
    conditionGroup: extractConditionGroup(model.filter_group),
    action: model.action ? extractAutomationAction(model.action) : undefined,
    dedup: model.dedup
      ? {
          keyTemplate: model.dedup.key_template ?? '',
          window: model.dedup.window ?? 0,
        }
      : undefined,
    retry: model.retry
      ? {
          maxAttempts: model.retry.max_attempts ?? 0,
          backoff: model.retry.backoff ?? '',
        }
      : undefined,
    rateLimit: model.rate_limit
      ? {
          maxActions: model.rate_limit.max_actions ?? 0,
          window: model.rate_limit.window ?? 0,
        }
      : undefined,
  };
}

/**
 * Serialize UI WorkflowRule partial into the update input shape for PUT calls.
 * Returns EvaluationRuleUpdateInput when triggers are present, RuleUpdateInput otherwise.
 */
export function serializeRuleUpdate(
  rule: Partial<WorkflowRule>
): EvaluationRuleUpdateInput & RuleUpdateInput {
  const update: EvaluationRuleUpdateInput & RuleUpdateInput = {};
  if (rule.name !== undefined) update.name = rule.name;
  if (rule.description !== undefined) update.description = rule.description;
  if (rule.enabled !== undefined) update.enabled = rule.enabled;
  if (rule.priority !== undefined) update.priority = rule.priority;
  if (rule.triggers !== undefined) {
    update.triggers = rule.triggers.map(serializeEvaluatorTrigger);
  }
  if (rule.trigger !== undefined) {
    update.trigger = {
      event_type: rule.trigger.eventType,
      event_space: rule.trigger.eventSpace,
    };
  }
  if (rule.filters !== undefined) {
    update.filters = rule.filters.map((f) => ({
      field: f.field,
      operator: f.operator,
      value: String(f.value),
    }));
  }
  if (rule.conditionGroup !== undefined) {
    update.filter_group = serializeConditionGroup(rule.conditionGroup);
  }
  if (rule.action !== undefined) {
    update.action = serializeAutomationAction(rule.action);
  }
  if (rule.dedup !== undefined) {
    update.dedup = {
      key_template: rule.dedup.keyTemplate,
      window: rule.dedup.window,
    };
  }
  if (rule.retry !== undefined) {
    update.retry = {
      max_attempts: rule.retry.maxAttempts,
      backoff: rule.retry.backoff,
    };
  }
  if (rule.rateLimit !== undefined) {
    update.rate_limit = {
      max_actions: rule.rateLimit.maxActions,
      window: rule.rateLimit.window,
    };
  }
  return update;
}
